import React, { FC } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import OnboardingShell from "./OnboardingShell";
import { Dictionary } from "./i18n/strings";
import { useLocale } from "./i18n/locale";

export interface TenantReadyForArrivalPageProps {
  className?: string;
  /** 4 for Flow A (short onboarding), 7 for Flow B (full onboarding). */
  totalSteps?: number;
}

const ShieldCheck = (
  <>
    <path d="M12 21.5c4.6-2 6.8-5.2 6.8-9.4V5.9L12 3.2 5.2 5.9v6.2c0 4.2 2.2 7.4 6.8 9.4z" />
    <path d="M9.2 12.2l2 2 3.6-3.8" />
  </>
);

// Person, shield-check, shield-check — as drawn in the mockup.
const getUpcoming = (t: Dictionary) => [
  {
    label: t.arrival.upcomingInventory,
    icon: (
      <>
        <circle cx="12" cy="8" r="3.4" />
        <path d="M6 19.5c0-3.3 2.7-5.6 6-5.6s6 2.3 6 5.6z" />
      </>
    ),
  },
  { label: t.arrival.upcomingInsurance, icon: ShieldCheck },
  { label: t.arrival.upcomingPractical, icon: ShieldCheck },
];

const TenantReadyForArrivalPage: FC<TenantReadyForArrivalPageProps> = ({
  className = "",
  totalSteps = 7,
}) => {
  const navigate = useNavigate();
  const { t } = useLocale();
  const upcoming = getUpcoming(t);

  // Flow A stops here: with no inventory or insurance to collect there are no
  // post-arrival steps to unlock, so the page is purely a confirmation.
  const hasPostArrivalSteps = totalSteps > 4;

  return (
    <div
      className={`nc-TenantReadyForArrivalPage ${className}`}
      data-nc-id="TenantReadyForArrivalPage"
    >
      <Helmet>
        <title>{t.arrival.documentTitle}</title>
      </Helmet>

      <OnboardingShell
        step={4}
        totalSteps={totalSteps}
        title={t.arrival.title}
        subtitle={t.arrival.subtitle}
      >
        <div className="flex justify-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full text-primary-6000 ring-1 ring-primary-6000">
            <svg
              className="h-14 w-14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={1.3}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              {/* Roof, chimney on the right pitch, walls, and a heart inside. */}
              <path d="M2.6 11.6L12 4l9.4 7.6" />
              <path d="M16.8 6.6V4.3h1.9v3.8" />
              <path d="M4.9 10v9.6h14.2V10" />
              <path d="M12 17c-2.5-1.7-3.7-2.9-3.7-4.2a2 2 0 013.7-1.1 2 2 0 013.7 1.1c0 1.3-1.2 2.5-3.7 4.2z" />
            </svg>
          </span>
        </div>

        <div className="mt-5 rounded-md bg-cream-100 border border-primary-6000/20 p-5">
          <p className="text-center font-semibold text-[13px] leading-relaxed text-neutral-600 px-10">
            {t.arrival.body}
          </p>

          <ul className="mt-4 flex flex-col gap-3">
            {upcoming.map(({ label, icon }) => (
              <li key={label} className="flex items-center gap-3">
                <svg
                  className="h-5 w-5 flex-none text-primary-6000"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  {icon}
                </svg>
                <span className="text-[13px] text-navy-900 font-medium">{label}</span>
              </li>
            ))}
          </ul>
        </div>

        {hasPostArrivalSteps && (
          <button
            type="button"
            onClick={() => navigate("/tenant/inventory")}
            className="mt-4 flex w-full items-center justify-between gap-3 rounded-md bg-navy-800 px-6 py-4 text-sm font-semibold tracking-wide text-white transition-colors hover:bg-navy-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-800 focus-visible:ring-offset-2"
          >
            <span className="flex-1 text-center">{t.arrival.cta}</span>
            <svg
              className="h-5 w-5 flex-none"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M5 12h18m0 0l-7-7m7 7l-7 7" />
            </svg>
          </button>
        )}
      </OnboardingShell>
    </div>
  );
};

export default TenantReadyForArrivalPage;
